import { cn } from '@rocco/utils/cn';
import type { ImageColumnProps } from '@modules/page-builder/types';
import { RevampImageFigure } from './RevampImageFigure';
import { RevampSectionHeading } from './RevampSectionHeading';
import {
    REVAMP_GALLERY_TRIO_SIZES,
    REVAMP_TILE_SIZES,
} from '../../util/revamp-image-sizes';

interface RevampGalleryModuleProps {
    columns: ImageColumnProps[];
    /** Tiles per desktop row: 3 (trio) or 4 (quad / octa two-row). */
    perRow: 3 | 4;
    /** Optional ruled heading, from the module's h3 title column. */
    titleHTML?: string;
}

/**
 * Image gallery grid (CMS presets "Single row gallery", "Two row gallery"):
 * square tiles per the column dimension, under the module's optional ruled
 * heading. Collection presets render via RevampCollectionModule instead.
 */
export const RevampGalleryModule = ({
    columns,
    perRow,
    titleHTML,
}: RevampGalleryModuleProps) => {
    const images = columns.filter((column) => column.imageUrl);
    if (images.length === 0) return null;

    return (
        <section className="w-full">
            <RevampSectionHeading contentHTML={titleHTML} />
            <div
                className={cn(
                    'grid grid-cols-2 gap-x-4 gap-y-10 px-4.5 md:gap-x-6 md:gap-y-[6.25rem] md:px-[3rem]',
                    perRow === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4',
                    titleHTML && 'mt-6 md:mt-[1.5rem]',
                )}
            >
                {images.map((column) => (
                    <RevampImageFigure
                        key={column.id}
                        column={column}
                        sizes={
                            perRow === 3
                                ? REVAMP_GALLERY_TRIO_SIZES
                                : REVAMP_TILE_SIZES
                        }
                        className="min-w-0"
                    />
                ))}
            </div>
        </section>
    );
};
